import type { TransmitterData } from "../context";
export type { TransmitterData };

/*
 * EXTRACTION NOTE: getTransmitterData() read the "transmittersData" table (highest
 * counterLimit wins). The engine has no database, so the caller passes the already
 * resolved transmitter in as ctx.transmitter. The interface moved to context.ts unchanged
 * and is re-exported above, so there is exactly one TransmitterData type.
 *
 * EXTRACTION NOTE: buildTransmitterGroup opened with a console.log of the whole transmitter
 * record. The engine does not write to the console, and that line printed credentials-adjacent
 * identity data on every build, so it is gone.
 */

export function buildTransmitterGroup(transmitterData: TransmitterData) {
  // Create object with elements in the correct schema-defined order
  const result: any = {
    // TIN must come first - required by schema
    TIN: transmitterData.transmitterTIN || "000000000",
    TINSubmittedTypeCd: "INDIVIDUAL_TIN",
    TransmitterControlCd: transmitterData.transmitterControlCode || "00000",
    ForeignEntityInd: transmitterData.foreignEntityIndicator || "0",
  };

  const formattedName = formatBusinessName(transmitterData.transmitterName);
  if (formattedName) {
    result.PersonNm = formattedName;
  }

  // CompanyGrp must come before any contact information
  const companyName = formatBusinessName(transmitterData.companyName);
  if (companyName) {
    result.CompanyGrp = {
      BusinessName: {
        BusinessNameLine1Txt: companyName,
      },
    };

    const companyNameContinuation = formatBusinessName(
      transmitterData.companyNameContinuation
    );
    if (companyNameContinuation) {
      result.CompanyGrp.BusinessName.BusinessNameLine2Txt =
        companyNameContinuation;
    }

    // MailingAddressGrp must come before ContactNameGrp - required by schema order
    const address = formatAddress(transmitterData.companyMailingAddress);
    const city = formatCity(transmitterData.companyCity);
    const state = transmitterData.companyState;
    const zip = formatZIP(transmitterData.companyZIPCode);

    if (address || city || state || zip) {
      result.CompanyGrp.MailingAddressGrp = {
        USAddress: {},
      };

      if (address)
        result.CompanyGrp.MailingAddressGrp.USAddress.AddressLine1Txt = address;
      if (city) result.CompanyGrp.MailingAddressGrp.USAddress.CityNm = city;
      if (state)
        result.CompanyGrp.MailingAddressGrp.USAddress.StateAbbreviationCd =
          state;
      if (zip) result.CompanyGrp.MailingAddressGrp.USAddress.ZIPCd = zip;
    }
  }

  // ContactNameGrp must come after MailingAddressGrp
  const contactName = formatBusinessName(transmitterData.contactName);
  if (contactName) {
    result.ContactNameGrp = {
      PersonNm: contactName,
    };
  }

  // ContactEmailAddressTxt and ContactPhoneNum must come after ContactNameGrp
  if (transmitterData.contactEmailAddress) {
    result.ContactEmailAddressTxt = transmitterData.contactEmailAddress;
  }

  const phone = formatPhone(transmitterData.contactTelephoneNumber);
  if (phone) {
    result.ContactPhoneNum = phone;
  }

  return result;
}

export function buildVendorGroup(transmitterData: TransmitterData) {
  // Create object with elements in the correct schema-defined order
  const result: any = {
    ForeignEntityInd: transmitterData.vendorForeignEntityIndicator || "0",
  };

  // BusinessName is REQUIRED before any other elements
  const vendorName = formatBusinessName(transmitterData.vendorName);
  result.BusinessName = {
    BusinessNameLine1Txt: vendorName || "Vendor Company",
  };

  // Add BusinessNameLine2Txt if needed
  if (transmitterData.companyNameContinuation) {
    result.BusinessName.BusinessNameLine2Txt = "DBA Vendor";
  }

  // MailingAddressGrp must come before ContactNameGrp - required by schema order
  const address = formatAddress(transmitterData.vendorMailingAddress);
  const city = formatCity(transmitterData.vendorCity);
  const state = transmitterData.vendorState;
  const zip = formatZIP(transmitterData.vendorZIPCode);

  if (address || city || state || zip) {
    result.MailingAddressGrp = {
      USAddress: {},
    };

    if (address) result.MailingAddressGrp.USAddress.AddressLine1Txt = address;
    if (city) result.MailingAddressGrp.USAddress.CityNm = city;
    if (state) result.MailingAddressGrp.USAddress.StateAbbreviationCd = state;
    if (zip) result.MailingAddressGrp.USAddress.ZIPCd = zip;
  } else {
    // If no address data, provide default values for required fields
    result.MailingAddressGrp = {
      USAddress: {
        AddressLine1Txt: "456 Vendor Rd",
        CityNm: "VendorCity",
        StateAbbreviationCd: "IL",
        ZIPCd: "60202",
      },
    };
  }

  // ContactNameGrp must come after MailingAddressGrp
  const contactName = formatBusinessName(transmitterData.vendorContactName);
  if (contactName) {
    result.ContactNameGrp = {
      PersonNm: contactName,
    };
  } else {
    // Add default ContactNameGrp if missing
    result.ContactNameGrp = {
      PersonNm: "Vendor Contact",
    };
  }

  // ContactEmailAddressTxt must come after ContactNameGrp
  if (transmitterData.contactEmailAddress) {
    result.ContactEmailAddressTxt = transmitterData.contactEmailAddress;
  }

  const phone = formatPhone(transmitterData.contactTelephoneNumber);
  if (phone) {
    result.ContactPhoneNum = phone;
  }

  return result;
}

export function buildIssuerDetail(transmitterData: TransmitterData) {
  // Create object with elements in the correct schema-defined order
  const result: any = {
    ForeignEntityInd: transmitterData.foreignEntityIndicator || "0",
    // TIN must come first - required by schema
    TIN:
      transmitterData.testFileIndicator === "T"
        ? "000000000"
        : transmitterData.IssuerTIN,
    TINSubmittedTypeCd: "INDIVIDUAL_TIN",
  };

  const firstName = formatBusinessName(transmitterData.transmitterName);
  const lastName = formatLastName(transmitterData.transmitterNameContinuation);

  // IssuerDetailType offers a choice here: a person's name, or a business's. A person needs
  // both halves - OtherCompletePersonNameType makes PersonFirstNm and PersonLastNm both
  // required - so an issuer known by a single name line is a business, and writing it as a
  // PersonName with no PersonLastNm produced XML the IRS schema refused. The keys are added
  // in schema order; the name control belongs to whichever branch is taken.
  if (firstName && lastName) {
    result.PersonNameControlTxt = "PAYS";
    result.PersonName = { PersonFirstNm: firstName, PersonLastNm: lastName };
  } else if (firstName || lastName) {
    // formatLastName leaves spaces that BusinessNameLine1Type does not take, so the one
    // name line is formatted as a business name whichever of the two it arrived in.
    result.BusinessName = {
      BusinessNameLine1Txt:
        firstName || formatBusinessName(transmitterData.transmitterNameContinuation),
    };
  }

  // MailingAddressGrp is required
  const address = formatAddress(transmitterData.IssuerShippingAddress);
  const city = formatCity(transmitterData.IssuerCity);
  const state = transmitterData.IssuerState;
  const zip = formatZIP(transmitterData.IssuerZIPCode);

  if (address || city || state || zip) {
    result.MailingAddressGrp = {
      USAddress: {},
    };

    if (address) result.MailingAddressGrp.USAddress.AddressLine1Txt = address;
    if (city) result.MailingAddressGrp.USAddress.CityNm = city;
    if (state) result.MailingAddressGrp.USAddress.StateAbbreviationCd = state;
    if (zip) result.MailingAddressGrp.USAddress.ZIPCd = zip;
  }

  const phone = formatPhone(transmitterData.IssuerTelephone);
  if (phone) {
    result.PhoneNum = phone;
  }

  return result;
}

// Helper functions for formatting
/*
 * The IRS types these three feed accept single spaces between words - BusinessNameLine1Type
 * is "(([A-Za-z0-9#\-\(\)]|&|') ?)*...", StreetAddressType "[A-Za-z0-9]( ?[A-Za-z0-9\-/])*",
 * CityType "([A-Za-z] ?)*[A-Za-z]" - and the formatters used to strip every space along with
 * the characters the types refuse. Schema-valid, and wrong: every issuer TabFile has filed
 * went to the IRS as RIDGEVIEWCONSULTINGLLC at 500OAKWOODPKWY, while the recipients on the
 * same return kept their spaces (TS-8327). Now the disallowed characters go, runs of spaces
 * become one, and the value is cut at the type's length on a word boundary where there is
 * one.
 */
function fitToType(value: string, allowed: RegExp, maxLength: number): string {
  const cleaned = value.toUpperCase().replace(allowed, "").replace(/\s+/g, " ").trim();
  if (cleaned.length <= maxLength) return cleaned;
  const cut = cleaned.slice(0, maxLength);
  const atWord = cut.lastIndexOf(" ");
  return (atWord > maxLength / 2 ? cut.slice(0, atWord) : cut).trim();
}

function formatBusinessName(name: string): string {
  if (!name) return "";
  return fitToType(name, /[^A-Z0-9#\-\(\)&' ]/g, 75);
}

function formatAddress(address: string): string {
  if (!address) return "";
  // A street address may not start with a hyphen or slash and may not carry two in a row.
  return fitToType(address, /[^A-Z0-9\-/ ]/g, 35).replace(/^[-/ ]+/, "").replace(/([-/]) (?=[-/])/g, "$1");
}

function formatCity(city: string): string {
  if (!city) return "";
  return fitToType(city, /[^A-Z ]/g, 40);
}

function formatZIP(zip: string): string {
  if (!zip) return "";
  const formatted = zip.replace(/[^0-9]/g, "").slice(0, 5);
  return formatted || "";
}

function formatPhone(phone: string): string {
  if (!phone) return "";
  // Remove all non-numeric characters and ensure it's a valid phone number
  const formatted = phone.replace(/[^0-9]/g, "").slice(0, 10);
  return formatted || "0000000000";
}

function formatLastName(name: string): string {
  if (!name) return "";
  // Ensure last name matches pattern ([A-Za-z\-] ?)*[A-Za-z\-]
  // Only allow letters, hyphens, and optional spaces
  // Must end with a letter or hyphen
  const formatted = name
    .replace(/[^A-Za-z\- ]/g, "") // Remove invalid characters
    .replace(/\s+/g, " ") // Normalize spaces
    .trim() // Remove leading/trailing spaces
    .replace(/\s+$/, ""); // Remove trailing spaces

  // If empty after formatting, return "LAST"
  if (!formatted) return "LAST";

  // Ensure it ends with a letter or hyphen
  if (!/[A-Za-z\-]$/.test(formatted)) {
    return formatted + "A";
  }

  return formatted;
}
